import { useState } from 'react';
import { Upload, AlertCircle } from 'lucide-react';
import { Modal, Form, Button, Alert } from '../ui';

interface ImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<void>;
  isLoading: boolean;
}

function ImportContactsModal({ isOpen, onClose, onImport, isLoading }: ImportContactsModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = e.target.files;

    if (!files || files.length === 0) {
      setSelectedFile(null);
      return;
    }

    const file = files[0];

    // Validate file type
    if (!file.name.endsWith('.txt') && !file.name.endsWith('.csv')) {
      setError('Apenas arquivos TXT ou CSV são permitidos');
      setSelectedFile(null);
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('O arquivo deve ter no máximo 5MB');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      setError('Por favor, selecione um arquivo');
      return;
    }

    try {
      await onImport(selectedFile);
      // Reset form after successful import
      setSelectedFile(null);
      if (e.target instanceof HTMLFormElement) {
        e.target.reset();
      }
    } catch (err: any) {
      // Display backend error if available
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao importar contatos. Tente novamente.');
      }
      console.error('Import error:', err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Importar Contatos" size="md">
      <Form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Arraste e solte um arquivo aqui ou
            </p>
            <input
              type="file"
              id="file-upload"
              className="hidden"
              onChange={handleFileChange}
              accept=".txt,.csv"
            />
            <label
              htmlFor="file-upload"
              className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 cursor-pointer"
            >
              Selecionar Arquivo
            </label>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">TXT ou CSV até 5MB</p>
          </div>

          {selectedFile && (
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md">
              <p className="text-sm font-medium">Arquivo selecionado:</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{selectedFile.name}</p>
            </div>
          )}

          {error && (
            <Alert variant="error" icon={AlertCircle}>
              {error}
            </Alert>
          )}

          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Formato esperado:
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              O arquivo deve conter uma linha por contato no formato:
            </p>
            <pre className="bg-gray-50 dark:bg-gray-800 p-2 rounded text-xs overflow-x-auto">
              contato,nome,canal,tipo
            </pre>
            <p className="text-sm text-gray-600 dark:text-gray-400">Exemplo:</p>
            <pre className="bg-gray-50 dark:bg-gray-800 p-2 rounded text-xs overflow-x-auto">
              5511999999999,João Silva,whatsapp,commercial
              <br />
              contato@email.com,Maria Souza,email,administrative
            </pre>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Canal</strong> (opcional): whatsapp, email, instagram, facebook, sms, phone, website, telegram, messenger, api, none, other
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Tipo</strong> (opcional): administrative, registration, commercial, financial, legal_representative, technical, other
            </p>
          </div>

          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={!selectedFile || isLoading}
              className="sm:col-start-2"
            >
              Importar
            </Button>
            <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
              Cancelar
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
}

export default ImportContactsModal;
